import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { BackButton } from '../components/ui/back-button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Edit, Save, Scale } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { legalAPI } from '../services/api';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const FIELDS = [
  { key: 'association_name', label: "Nom de l'association" },
  { key: 'legal_form', label: 'Forme juridique' },
  { key: 'rna_number', label: 'Numéro RNA' },
  { key: 'siren_number', label: 'Numéro SIREN (optionnel)' },
  { key: 'address', label: 'Adresse du siège social' },
  { key: 'contact_email', label: 'Email de contact' },
  { key: 'phone', label: 'Téléphone' },
  { key: 'publisher_name', label: 'Responsable de la publication' },
  { key: 'publisher_title', label: 'Fonction du responsable' },
  { key: 'host_name', label: 'Hébergeur' },
  { key: 'host_details', label: "Coordonnées de l'hébergeur" },
  { key: 'dpo_email', label: 'Email contact données personnelles' },
  { key: 'data_retention', label: 'Durée de conservation des données', multiline: true },
];

const Section = ({ title, children, testId }) => (
  <Card className="bg-gradient-to-br from-purple-900/30 to-black border-2 border-purple-500/30 p-6 md:p-8 space-y-3" data-testid={testId}>
    <h2 className="text-lg md:text-xl font-bold text-purple-300">{title}</h2>
    <div className="text-gray-300 text-sm md:text-base leading-relaxed space-y-2">{children}</div>
  </Card>
);

const Legal = () => {
  const { isAdmin } = useAuth();
  const [info, setInfo] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    legalAPI.get().then(setInfo).catch(() => toast.error('Erreur lors du chargement des mentions légales'));
  }, []);

  const openEdit = () => {
    setForm(info);
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await legalAPI.update(form);
      setInfo(updated);
      setEditOpen(false);
      toast.success('Mentions légales mises à jour');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (!info) {
    return (
      <div className="min-h-screen pt-24 px-4 pb-12 flex items-center justify-center">
        <div className="text-purple-400 text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 pb-12" data-testid="legal-page">
      <SEO title="Mentions légales" description="Mentions légales et politique de confidentialité du site de l'association Just Rôle It." path="/mentions-legales" />
      <div className="container mx-auto max-w-4xl space-y-6">
        <BackButton />

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 mb-2 tracking-wide">
              Mentions légales
            </h1>
            <p className="text-gray-400">Informations légales et protection des données personnelles</p>
          </div>
          {isAdmin() && (
            <Button onClick={openEdit} data-testid="legal-edit-button" className="bg-purple-600 hover:bg-purple-500 text-white">
              <Edit className="mr-2 h-4 w-4" /> Modifier
            </Button>
          )}
        </div>

        <Section title="1. Éditeur du site" testId="legal-section-editor">
          <p><strong className="text-purple-200">{info.association_name}</strong> — {info.legal_form}</p>
          <p>Numéro RNA : <span data-testid="legal-rna">{info.rna_number}</span>{info.siren_number && <> — SIREN : {info.siren_number}</>}</p>
          <p>Siège social : {info.address}</p>
          <p>Email : <a href={`mailto:${info.contact_email}`} className="text-purple-400 underline">{info.contact_email}</a> — Téléphone : {info.phone}</p>
          <p>Responsable de la publication : <span data-testid="legal-publisher">{info.publisher_name}</span>, {info.publisher_title}.</p>
        </Section>

        <Section title="2. Hébergement" testId="legal-section-host">
          <p>Le site est hébergé par <strong className="text-purple-200">{info.host_name}</strong>.</p>
          <p>{info.host_details}</p>
        </Section>

        <Section title="3. Propriété intellectuelle" testId="legal-section-ip">
          <p>
            L'ensemble des contenus de ce site (textes, logo, images, illustrations) est la propriété de {info.association_name}
            ou de leurs auteurs respectifs et est protégé par le droit d'auteur. Toute reproduction, même partielle, est interdite
            sans autorisation écrite préalable.
          </p>
          <p>
            Les noms de jeux, systèmes et univers cités appartiennent à leurs éditeurs respectifs. Les jeux référencés dans la
            ludothèque le sont à titre informatif, sans lien d'affiliation.
          </p>
        </Section>

        <Section title="4. Données personnelles (RGPD)" testId="legal-section-rgpd">
          <p>
            Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés,
            {` ${info.association_name}`} traite les données personnelles suivantes :
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-purple-200">Compte utilisateur</strong> : nom, adresse email, mot de passe (chiffré), avatar.</li>
            <li><strong className="text-purple-200">Profil adhérent</strong> (facultatif) : téléphone, adresse postale, pseudo Discord, préférences de jeu.</li>
            <li><strong className="text-purple-200">Formulaire de contact</strong> : nom, email, contenu du message.</li>
            <li><strong className="text-purple-200">Statistiques de fréquentation</strong> : pages consultées, horodatage, navigateur (sans cookie tiers).</li>
          </ul>
          <p>
            <strong className="text-purple-200">Finalités</strong> : gestion des adhésions et de la vie associative, organisation
            des séances de jeu, communication entre membres, réponse aux demandes de contact et amélioration du site.
          </p>
          <p>
            <strong className="text-purple-200">Destinataires</strong> : les membres du bureau et les administrateurs du site.
            Le téléphone et l'adresse postale ne sont visibles que par le bureau et les administrateurs. Aucune donnée n'est
            vendue ni transmise à des tiers, hors prestataires techniques nécessaires (hébergement, envoi d'emails).
          </p>
          <p><strong className="text-purple-200">Durée de conservation</strong> : {info.data_retention}</p>
          <p>
            <strong className="text-purple-200">Vos droits</strong> : vous disposez d'un droit d'accès, de rectification,
            d'effacement, de limitation et d'opposition sur vos données. Vous pouvez modifier vos informations depuis votre
            profil ou exercer vos droits en écrivant à{' '}
            <a href={`mailto:${info.dpo_email}`} className="text-purple-400 underline">{info.dpo_email}</a>.
            En cas de litige, vous pouvez saisir la CNIL (<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-purple-400 underline">www.cnil.fr</a>).
          </p>
        </Section>

        <Section title="5. Cookies" testId="legal-section-cookies">
          <p>
            Le site utilise uniquement un cookie de session strictement nécessaire à l'authentification des membres, ainsi
            qu'un stockage local pour mémoriser votre préférence de thème (clair/sombre). Aucun cookie publicitaire ou de
            suivi tiers n'est déposé. Ces cookies ne nécessitent pas de consentement préalable.
          </p>
        </Section>

        <Section title="6. Contact" testId="legal-section-contact">
          <p>
            Pour toute question relative au site ou à vos données, utilisez notre{' '}
            <Link to="/contact" className="text-purple-400 underline">formulaire de contact</Link> ou écrivez à{' '}
            <a href={`mailto:${info.contact_email}`} className="text-purple-400 underline">{info.contact_email}</a>.
          </p>
          {info.updated_at && (
            <p className="text-xs text-gray-500">Dernière mise à jour : {new Date(info.updated_at).toLocaleDateString('fr-FR')}</p>
          )}
        </Section>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-black border-2 border-purple-500/50 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-purple-300 flex items-center gap-2"><Scale className="h-5 w-5" /> Modifier les mentions légales</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {FIELDS.map(({ key, label, multiline }) => (
              <div key={key} className="space-y-1">
                <Label htmlFor={`legal-${key}`} className="text-purple-300 text-sm">{label}</Label>
                {multiline ? (
                  <Textarea id={`legal-${key}`} data-testid={`legal-input-${key}`} rows={3} value={form[key] || ''}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="bg-black/50 border-purple-500/30 text-white" />
                ) : (
                  <Input id={`legal-${key}`} data-testid={`legal-input-${key}`} value={form[key] || ''}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="bg-black/50 border-purple-500/30 text-white" />
                )}
              </div>
            ))}
            <Button onClick={handleSave} disabled={saving} data-testid="legal-save-button" className="w-full bg-purple-600 hover:bg-purple-500 text-white">
              <Save className="mr-2 h-4 w-4" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Legal;
